
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Engine } from '@tsparticles/engine';
import { initParticlesEngine } from './particles-utils';

interface ParticlesContextType {
  isInitialized: boolean;
  isLoading: boolean;
  error: Error | null;
  isProduction: boolean;
  usingFallback: boolean;
  engine?: Engine | null;
  setEngine?: (engine: Engine) => void;
}

const ParticlesContext = createContext<ParticlesContextType>({
  isInitialized: false,
  isLoading: true,
  error: null,
  isProduction: import.meta.env.PROD,
  usingFallback: false,
});

export const ParticlesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [engine, setEngine] = useState<Engine | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const isProduction = import.meta.env.PROD;

  useEffect(() => {
    // Initialize the particles engine once when the provider mounts
    const init = async () => {
      try {
        const success = await initParticlesEngine();
        setIsInitialized(success);
        if (!success) {
          setUsingFallback(true);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error initializing particles'));
        setUsingFallback(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    init();
  }, []);

  return (
    <ParticlesContext.Provider value={{ 
      isInitialized, 
      isLoading,
      error,
      isProduction,
      usingFallback,
      engine,
      setEngine: (newEngine: Engine) => setEngine(newEngine) 
    }}>
      {children}
    </ParticlesContext.Provider>
  );
};

export const useParticles = () => useContext(ParticlesContext);
